import {
  closestCenter,
  DndContext,
  DragEndEvent,
  DragOverlay,
  DragStartEvent,
  KeyboardSensor,
  PointerSensor,
  useDroppable,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import {
  ChevronLeft,
  ChevronRight,
  Comment,
  Lock,
  Description,
} from "@mui/icons-material";
import {
  Avatar,
  Box,
  Card,
  CardContent,
  Chip,
  Paper,
  Tooltip,
  Typography,
} from "@mui/material";
import { motion } from "framer-motion";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { RootState } from "../../../../../store/Index";
import thumbnailTemplate from "../../../../../assets/thumbnailTemplate1.jpeg";
import { IMemorialItem } from "../../../../../types/memory-table";

export interface KanbanStreamInput {
  stream_id: number;
  name_of_deceased: string;
  memory_book_id: number | null;
  memory_book_status: MemoryBookStatus | null;
  is_delivered: boolean;
  is_memory_book_available: boolean;
  total_comments: number;
  last_updated_by?: { first_name?: string; last_name?: string } | null;
}

export type MemoryBookStatus = "pending" | "in-progress" | "done" | "printing";

export type MemoryBookColumnId =
  | "not_available"
  | MemoryBookStatus
  | "delivered";

const DRAGGABLE_FROM: ReadonlySet<MemoryBookColumnId> = new Set([
  "done",
  "printing",
]);

const ALLOWED_MOVES: Partial<Record<MemoryBookColumnId, MemoryBookColumnId>> =
  {
    done: "printing",
    printing: "delivered",
  };

const deriveColumnId = (stream: KanbanStreamInput): MemoryBookColumnId => {
  if (stream.is_delivered) return "delivered";
  if (!stream.memory_book_id || !stream.memory_book_status)
    return "not_available";
  return stream.memory_book_status;
};

interface KanbanCardItem extends IMemorialItem {
  stream_id: number;
  memory_book_id: number | null;
  status: MemoryBookColumnId;
  is_memory_book_available: boolean;
}

interface KanbanColumnData {
  id: MemoryBookColumnId;
  title: string;
  description: string;
  items: KanbanCardItem[];
  collapsed?: boolean;
}

const BASE_COLUMNS: Omit<KanbanColumnData, "items">[] = [
  {
    id: "not_available",
    title: "Not Available",
    description: "No one has been assigned to this stream's memory book yet.",
  },
  {
    id: "pending",
    title: "Pending",
    description: "Assigned, but work hasn't started yet.",
  },
  {
    id: "in-progress",
    title: "In Progress",
    description: "Comment counts are being scanned and uploaded.",
  },
  {
    id: "done",
    title: "Done",
    description: "All comments have been scanned and uploaded.",
  },
  {
    id: "printing",
    title: "Printing",
    description: "Sent to print.",
  },
  {
    id: "delivered",
    title: "Delivered",
    description: "The memory book has been delivered to the client.",
  },
];

const buildCardsFromStreams = (
  streams: KanbanStreamInput[],
): KanbanCardItem[] =>
  streams.map((stream) => ({
    id: stream.stream_id.toString(),
    stream_id: stream.stream_id,
    memory_book_id: stream.memory_book_id,
    title: stream.name_of_deceased,
    priority: "Low",
    author:
      `${stream.last_updated_by?.first_name || ""} ${stream.last_updated_by?.last_name || ""}`.trim() ||
      "Unknown",
    comments: stream.total_comments,
    avatar: "/api/placeholder/32/32",
    status: deriveColumnId(stream),
    is_memory_book_available: stream.is_memory_book_available,
  }));

const buildColumns = (streams: KanbanStreamInput[]): KanbanColumnData[] => {
  const cards = buildCardsFromStreams(streams);
  return BASE_COLUMNS.map((col) => {
    const items = cards.filter((card) => card.status === col.id);
    return { ...col, items };
  });
};

// Sortable Card
const SortableCard = React.memo(
  ({
    item,
    isDragging,
    onNavigate,
  }: {
    item: KanbanCardItem;
    isDragging: boolean;
    onNavigate: (id: number) => void;
  }) => {
    const locked = !DRAGGABLE_FROM.has(item.status);
    const { attributes, listeners, setNodeRef, transform, transition } =
      useSortable({
        id: item.id,
        disabled: locked,
      });

    const style = {
      transform: CSS.Transform.toString(transform),
      transition,
      opacity: isDragging ? 0.5 : 1,
    };

    const reduxTheme = useSelector((state: RootState) => state.theme);

    return (
      <div
        ref={setNodeRef}
        style={style}
        {...(locked ? {} : { ...attributes, ...listeners })}
      >
        <Card
          onClick={() => onNavigate(item.stream_id)}
          sx={{
            mb: 2,
            cursor: locked ? "pointer" : "grab",
            boxShadow: 0,
            backgroundColor: reduxTheme.primary_colors[800],
            borderRadius: 5,
            "&:active": { cursor: locked ? "pointer" : "grabbing" },
          }}
        >
          <CardContent sx={{ p: 2 }}>
            <Box
              display="flex"
              justifyContent="space-between"
              alignItems="flex-start"
              mb={1}
            >
              <Box sx={{ display: "flex", gap: 0.5, alignItems: "center" }}>
                <Chip
                  label={item.priority}
                  size="small"
                  color={item.priority === "High" ? "warning" : "default"}
                  sx={{ fontSize: "0.75rem" }}
                />
                {item.is_memory_book_available && (
                  <Tooltip title="Book file uploaded">
                    <Description fontSize="small" color="success" />
                  </Tooltip>
                )}
              </Box>
              {locked && (
                <Tooltip title="System-tracked - moves automatically">
                  <Lock fontSize="small" color="disabled" />
                </Tooltip>
              )}
            </Box>
            <Typography
              variant="body2"
              mb={2}
              sx={{ color: reduxTheme.primary_colors[100], fontWeight: "bold" }}
            >
              {item.title}
            </Typography>
            <Box
              sx={{
                width: "100%",
                height: 120,
                borderRadius: 1,
                overflow: "hidden",
                mb: 2,
              }}
            >
              <img
                src={item.avatar || thumbnailTemplate}
                alt="Preview"
                style={{
                  width: "100%",
                  height: "100%",
                  objectFit: "cover",
                  display: "block",
                }}
              />
            </Box>
            <Box
              display="flex"
              alignItems="center"
              justifyContent="space-between"
            >
              <Box display="flex" alignItems="center" gap={0.5}>
                <Avatar src={item.avatar} sx={{ width: 20, height: 20 }} />
                <Typography
                  variant="caption"
                  sx={{
                    color: reduxTheme.primary_colors[100],
                    fontWeight: "bold",
                  }}
                >
                  {item.author}
                </Typography>
              </Box>
              <Box display="flex" alignItems="center" gap={0.5}>
                <Comment fontSize="small" color="action" />
                <Typography
                  variant="caption"
                  sx={{
                    color: reduxTheme.primary_colors[100],
                    fontWeight: "bold",
                  }}
                >
                  {item.comments} Comments
                </Typography>
              </Box>
            </Box>
          </CardContent>
        </Card>
      </div>
    );
  },
);

SortableCard.displayName = "SortableCard";

// Column
const KanbanColumn = React.memo(
  ({
    column,
    activeId,
    onToggle,
    onNavigate,
  }: {
    column: KanbanColumnData;
    activeId: string | null;
    onToggle: (id: string) => void;
    onNavigate: (id: number) => void;
  }) => {
    const hasItems = column.items.length > 0;
    const reduxTheme = useSelector((state: RootState) => state.theme);
    const isDropTarget =
      column.id === "printing" || column.id === "delivered";
    const { setNodeRef, isOver } = useDroppable({
      id: column.id,
      disabled: !isDropTarget,
    });

    return (
      <Paper
        ref={setNodeRef}
        component={motion.div}
        animate={{ width: column.collapsed ? 64 : 300 }}
        transition={{ type: "spring", stiffness: 300, damping: 30 }}
        sx={{
          p: column.collapsed ? 1 : 2,
          overflow: "hidden",
          minHeight: 240,
          boxShadow: 0,
          bgcolor: isOver ? "action.hover" : "background.paper",
          borderRadius: 3,
          display: "flex",
          flexDirection: "column",
        }}
      >
        <Box sx={{ mb: 1 }}>
          {!column.collapsed && (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Tooltip title="Collapse column">
                  <Box
                    component={motion.div}
                    onClick={() => onToggle(column.id)}
                    whileHover={{ scale: 1.15 }}
                    whileTap={{ scale: 0.9 }}
                    sx={{
                      width: 22,
                      height: 22,
                      borderRadius: "50%",
                      bgcolor: reduxTheme.primary_colors[400],
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      cursor: "pointer",
                      flexShrink: 0,
                    }}
                  >
                    <ChevronLeft sx={{ fontSize: 16, color: "#fff" }} />
                  </Box>
                </Tooltip>
                <Typography variant="subtitle1" fontWeight={600} noWrap>
                  {column.title}
                </Typography>
              </Box>
              <Chip
                label={column.items.length}
                size="small"
                sx={{
                  backgroundColor: reduxTheme.primary_colors[400],
                  color: reduxTheme.primary_colors[900],
                  minWidth: 24,
                }}
              />
            </Box>
          )}

          {column.collapsed && (
            <Box
              sx={{
                height: "100%",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
              }}
            >
              <Tooltip title="Expand column">
                <Box
                  component={motion.div}
                  onClick={() => onToggle(column.id)}
                  whileHover={{ scale: 1.15 }}
                  whileTap={{ scale: 0.9 }}
                  sx={{
                    width: 22,
                    height: 22,
                    borderRadius: "50%",
                    bgcolor: reduxTheme.primary_colors[400],
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    cursor: "pointer",
                    mb: 1,
                  }}
                >
                  <ChevronRight sx={{ fontSize: 16, color: "#fff" }} />
                </Box>
              </Tooltip>
              <Box
                sx={{
                  flexGrow: 1,
                  display: "flex",
                  alignItems: "center",
                  writingMode: "vertical-rl",
                  transform: "rotate(180deg)",
                  pointerEvents: "none",
                }}
              >
                <Typography
                  variant="subtitle2"
                  fontWeight={600}
                  letterSpacing={1}
                  color="text.secondary"
                  whiteSpace="nowrap"
                >
                  {column.title.toUpperCase()}
                </Typography>
              </Box>
            </Box>
          )}
        </Box>

        {!column.collapsed && (
          <>
            <Typography variant="body2" color="text.secondary" mb={2}>
              {column.description}
            </Typography>

            <Box
              sx={{
                flexGrow: 1,
                minHeight: hasItems ? "auto" : 140,
                display: "flex",
                flexDirection: "column",
                gap: 2,
              }}
            >
              <SortableContext
                items={column.items.map((item) => item.id)}
                strategy={verticalListSortingStrategy}
              >
                {column.items.map((item) => (
                  <SortableCard
                    key={item.id}
                    item={item}
                    isDragging={activeId === item.id}
                    onNavigate={onNavigate}
                  />
                ))}
              </SortableContext>

              {!hasItems && (
                <Box
                  sx={{
                    flexGrow: 1,
                    border: `2px dashed`,
                    borderColor: isOver ? "primary.main" : "grey.400",
                    borderRadius: 3,
                    minHeight: 120,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    mt: 2,
                    bgcolor: "background.paper",
                    transition: "border-color 0.2s ease",
                  }}
                >
                  <Typography
                    variant="body2"
                    color={isOver ? "primary.main" : "text.secondary"}
                  >
                    {isDropTarget
                      ? isOver
                        ? "Drop here"
                        : "Drop items here"
                      : "System-tracked"}
                  </Typography>
                </Box>
              )}
            </Box>
          </>
        )}
      </Paper>
    );
  },
);

KanbanColumn.displayName = "KanbanColumn";

interface MemoryBookKanbanProps {
  streams: KanbanStreamInput[];

  /**
   * Done -> Printing: PATCH { status: "printing" } on the MemoryBook.
   * Printing -> Delivered: PATCH { is_delivered: true } on the MemoryBook.
   * memoryBookId is guaranteed non-null here since both source columns only
   * contain cards that already have a MemoryBook row. Reject/throw to roll
   * the UI back.
   */
  onColumnChange?: (params: {
    streamId: number;
    memoryBookId: number;
    fromColumn: MemoryBookColumnId;
    toColumn: MemoryBookColumnId;
  }) => Promise<void>;
}

const MemoryBookKanban: React.FC<MemoryBookKanbanProps> = ({
  streams,
  onColumnChange,
}) => {
  const [columns, setColumns] = useState<KanbanColumnData[]>(() =>
    buildColumns(streams),
  );
  const [activeId, setActiveId] = useState<string | null>(null);
  const navigate = useNavigate();

  const kanbanScrollRef = useRef<HTMLDivElement | null>(null);
  const lastDeltaXRef = useRef(0);

  const COLUMN_WIDTH = 300;
  const COLUMN_GAP = 16;
  const SNAP_SIZE = COLUMN_WIDTH + COLUMN_GAP;

  useEffect(() => {
    setColumns(buildColumns(streams));
  }, [streams]);

  const activeItem = useMemo(
    () =>
      columns.flatMap((c) => c.items).find((i) => i.id === activeId) ?? null,
    [columns, activeId],
  );

  const toggleColumn = (columnId: string) => {
    setColumns((prev) =>
      prev.map((col) =>
        col.id === columnId ? { ...col, collapsed: !col.collapsed } : col,
      ),
    );
  };

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  const snapToColumn = (index: number) => {
    const container = kanbanScrollRef.current;
    if (!container) return;
    const maxScrollLeft = container.scrollWidth - container.clientWidth;
    const target = Math.max(0, Math.min(index * SNAP_SIZE, maxScrollLeft));
    container.scrollTo({ left: target, behavior: "smooth" });
  };

  const handleDragMove = ({ delta }: { delta: { x: number } }) => {
    const container = kanbanScrollRef.current;
    if (!container) return;
    const diffX = delta.x - lastDeltaXRef.current;
    if (Math.abs(diffX) > 1) {
      container.scrollLeft += diffX * 0.6;
    }
    lastDeltaXRef.current = delta.x;
  };

  const handleDragStart = (event: DragStartEvent) => {
    setActiveId(event.active.id as string);
    lastDeltaXRef.current = 0;
  };

  const moveCardLocally = (
    activeCardId: string,
    overId: string,
    destColId: string,
    patch: Partial<KanbanCardItem>,
  ) => {
    setColumns((prev) => {
      const newColumns = prev.map((c) => ({ ...c, items: [...c.items] }));
      const sourceCol = newColumns.find((c) =>
        c.items.some((i) => i.id === activeCardId),
      );
      const dest = newColumns.find((c) => c.id === destColId);
      if (!sourceCol || !dest) return prev;

      const itemIndex = sourceCol.items.findIndex((i) => i.id === activeCardId);
      const [moved] = sourceCol.items.splice(itemIndex, 1);
      Object.assign(moved, patch);

      const overIndex = dest.items.findIndex((i) => i.id === overId);
      if (overIndex === -1) {
        dest.items.push(moved);
      } else {
        dest.items.splice(overIndex, 0, moved);
      }

      const destIndex = newColumns.findIndex((c) => c.id === destColId);
      requestAnimationFrame(() => snapToColumn(destIndex));

      return newColumns;
    });
  };

  const handleDragEnd = async (event: DragEndEvent) => {
    const { active, over } = event;
    lastDeltaXRef.current = 0;

    if (!over || active.id === over.id) {
      setActiveId(null);
      return;
    }

    const allItems = columns.flatMap((c) => c.items);
    const movedItem = allItems.find((i) => i.id === active.id);
    const destCol =
      columns.find((c) => c.items.some((i) => i.id === over.id)) ??
      columns.find((c) => c.id === over.id);
    const sourceCol = columns.find((c) =>
      c.items.some((i) => i.id === active.id),
    );

    setActiveId(null);

    if (!movedItem || !destCol || !sourceCol) return;

    const fromColumn = sourceCol.id;
    const toColumn = destCol.id;

    if (fromColumn === toColumn) return;

    // Only Done -> Printing and Printing -> Delivered are allowed.
    if (ALLOWED_MOVES[fromColumn] !== toColumn) return;

    // Both allowed source columns only ever contain cards with a real
    // MemoryBook row, so this should always be present - guard anyway.
    if (!movedItem.memory_book_id) return;

    const previousColumns = columns.map((c) => ({ ...c, items: [...c.items] }));

    moveCardLocally(active.id as string, over.id as string, destCol.id, {
      status: toColumn,
    });

    if (onColumnChange) {
      try {
        await onColumnChange({
          streamId: movedItem.stream_id,
          memoryBookId: movedItem.memory_book_id,
          fromColumn,
          toColumn,
        });
      } catch {
        setColumns(previousColumns);
      }
    }
  };

  const handleDragCancel = () => {
    setActiveId(null);
    lastDeltaXRef.current = 0;
  };

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragStart={handleDragStart}
      onDragMove={handleDragMove}
      onDragEnd={handleDragEnd}
      onDragCancel={handleDragCancel}
    >
      <Box
        ref={kanbanScrollRef}
        display="flex"
        gap={2}
        sx={{ overflowX: "auto", pb: 2 }}
      >
        {columns.map((column) => (
          <motion.div
            key={column.id}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3 }}
          >
            <KanbanColumn
              column={column}
              activeId={activeId}
              onToggle={toggleColumn}
              onNavigate={(id) => navigate(`/memory-table/view/${id}`)}
            />
          </motion.div>
        ))}
      </Box>

      <DragOverlay>
        {activeItem ? (
          <Card
            sx={{
              width: 300,
              cursor: "grabbing",
              boxShadow: 20,
              borderRadius: 5,
            }}
          >
            <CardContent sx={{ p: 2 }}>
              <Typography variant="body2" mb={2} fontWeight="bold">
                {activeItem.title}
              </Typography>
              <Box
                sx={{
                  width: "100%",
                  height: 120,
                  borderRadius: 1,
                  overflow: "hidden",
                  mb: 2,
                }}
              >
                <img
                  src={activeItem.avatar || thumbnailTemplate}
                  alt="Preview"
                  style={{
                    width: "100%",
                    height: "100%",
                    objectFit: "cover",
                    display: "block",
                  }}
                />
              </Box>
              <Typography variant="caption" color="text.secondary">
                {activeItem.author} · {activeItem.comments} Comments
              </Typography>
            </CardContent>
          </Card>
        ) : null}
      </DragOverlay>
    </DndContext>
  );
};

export default MemoryBookKanban;